import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonWrapper } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    image = "image",
    description = "description",
    position = "position",
    backgroundcolor = "backgroundcolor",
    variant = "variant",
    label = "label",
    buttonurl = "buttonurl",
    link = "link",
    alignment = "alignment",
    disableButton = "disableButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.image]: string;
        [fields.description]: string;
        [fields.position]: string;
        [fields.backgroundcolor]: string;
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.buttonurl]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
        [fields.disableButton]: boolean;
    };
    extendedStyles?: DescriptionWidgetStyles;
}

export interface DescriptionWidgetStyles {
    Title?: InjectableCss;
    wrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    bannerImage?: LazyImageProps;
    bannerLink?: LinkPresentationProps;
    Content?: InjectableCss;
}

export const descriptionWidgetStyles: DescriptionWidgetStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            gap: 20px;
            ${ButtonWrapper} {
                background: ${getColor("primary")};
                border: none;
                font-size: 0.875rem;
                border-radius: 3px;
                width: 100%;
                padding: 17px 41px;
                color: ${getColor("primary.contrast")};
                display: inline-block;
                text-align: center;
                &:hover {
                    background: #6d6e71;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 0.875rem;
                        `,
                        css`
                            font-size: 0.875rem;
                        `,
                        css`
                            font-size: 0.875rem;
                        `,
                        null,
                        null,
                    ])}

                span {
                    font-family: "Roboto-Bold";
                }
            }
            .banner-subheading {
                h1 {
                    font-weight: 600;
                    margin-top: 29px;
                    font-size: 24px;
                    width: 100%;
                    margin-bottom: 20px;
                    color: ${getColor("primary")};
                    font-family: "Roboto-medium";
                    padding: 0;
                }
                ul {
                    /* padding: 0 !important; */
                }
                p {
                    font-size: 16px;
                    text-align: justify;
                    font-family: "Roboto-Regular";
                    color: ${getColor("text.accent")};
                    margin-bottom: 10px !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                margin-bottom: 10px;
                                text-align: justify;
                            `,
                            css`
                                margin-bottom: 10px;
                                text-align: justify;
                            `,
                            css`
                                margin-bottom: 10px;
                                text-align: justify;
                            `,
                            null,
                            null,
                        ])}

                    a {
                        span {
                            color: ${getColor("primary")} !important;
                        }

                        &:hover {
                            color: ${getColor("primary")};
                            text-decoration: underline;
                        }
                    }
                }
            }
        `,
    },
    Title: {
        css: css`
            text-align: center;
            color: ${getColor("common.backgroundContrast")};
            max-width: 60rem;
            width: 100%;
            margin: 0 auto;
            float: none;
            font-size: 28px;
            font-family: "Roboto-bold";
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 24px;
                        text-align: center;
                        margin-top: 0;
                    `,
                    css`
                        font-size: 24px;
                        text-align: center;
                        margin-top: 0;
                    `,
                    css`
                        font-size: 24px;
                        text-align: center;
                        margin-top: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            height: 100%;
            display: flex;
            flex-direction: column;
            /* justify-content: center; */
            align-items: flex-start;
            max-width: 96%;
            margin-left: auto;
            margin-top: -10px;
            padding-right: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0px 0px;
                            margin: 0px;
                            padding-left: 0px !important;
                            padding-right: 0px !important;
                        `,
                        css`
                            padding: 0px 0px;
                            margin: 0px;
                            padding-left: 0px !important;
                            padding-right: 0px !important;
                        `,
                        css`
                            padding: 0px 0px;
                            max-width: 100% !important;
                            margin: 0px auto;
                            padding-left: 0px !important;
                            padding-right: 0px !important;
                        `,
                        css`
                            padding-left: 30px;
                            padding-right: 15px;
                        `,
                        css`
                            padding-left: 57px;
                            padding-right: 15px;
                        `,
                    ],
                    "max",
                )}

            ul {
                li {
                    margin: 12px 0;
                    font-size: 14.4px;
                    text-align: justify;
                    font-family: "Roboto-medium";

                    span {
                        font-size: 14.4px;
                        font-family: "Roboto-medium";
                        color: ${getColor("primary")};
                        text-decoration: underline;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    font-size: 14.4px;
                                `,
                                css`
                                    font-size: 14.4px;
                                `,
                                css``,
                                null,
                                null,
                            ])}
                    }
                }
            }
        `,
    },
    Content: {
        css: css`
            width: 60%;
            padding-top: 0;
            padding-left: 0px;
            padding-right: 22px;
            padding-bottom: 34px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                        width: 100%;
                        padding: 0 14px !important;
                    `,
                    css`
                        display: block;
                        width: 100%;
                        padding: 0 14px !important;
                    `,
                    css`
                        display: block;
                        width: 100%;
                        padding: 0 14px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

export const DetailImageWidget: FC<OwnProps> = ({ id, fields, extendedStyles }) => {
    const styles = useMergeStyles("banner", descriptionWidgetStyles, extendedStyles);

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}

            height: 100%;
            margin-top: 45px;
            //margin-bottom: 53px;
            ${fields.position === "left" &&
            `
                                        display: flex;
                                        flex-direction: row;`};
            ${fields.position === "right" &&
            `
                                        display: flex;
                                        flex-direction: row-reverse;`};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                        width: 100%;
                        margin-bottom: 30px;
                    `,
                    css`
                        display: block;
                        width: 100%;
                        margin-bottom: 30px;
                    `,
                    css`
                        display: block;
                        width: 100%;
                        margin-bottom: 30px;
                    `,
                    null,
                    null,
                ])}
        `,
    };
    const imageOverlayStyles = {
        css: css`
            width: 35%;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                ])}

            img {
                height: 100%;
                width: 90%;
                padding: 15px 0;
                ${fields.position === "left" &&
                `
                                       width:100%`};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            display: block;
                            width: 100%;
                            margin-bottom: 30px;
                        `,
                        css`
                            display: block;
                            width: 100%;
                            margin-bottom: 30px;
                        `,
                        css`
                            display: block;
                            width: 100%;
                            margin-bottom: 30px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    };

    return (
        <StyledWrapper
            css={css`
                ${fields.backgroundcolor === "yes" && ` background:#f6f6f6;`};
                ${fields.backgroundcolor === "no" && ` background:none;`};
            `}
        >
            <StyledWrapper {...styles.Title}>{fields.title}</StyledWrapper>

            <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
                <LazyImage src={fields.image} {...imageOverlayStyles} />
                <StyledWrapper {...styles.Content}>
                    <StyledWrapper className="banner-center-wrapper" {...styles.slideCenteringWrapperStyles}>
                        <Typography className="banner-subheading">
                            {parse(fields.description, parserOptions)}
                        </Typography>
                        {!fields.disableButton && (
                            <ContentWrapper alignment={fields.alignment}>
                                <Link href={fields.buttonurl} target="_self">
                                    <Button className="button" type="submit">
                                        {fields.label}
                                    </Button>
                                </Link>
                            </ContentWrapper>
                        )}
                        <Zone zoneName="Code Snippet" contentId={id} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};
export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const detailImageWidget: WidgetModule = {
    component: DetailImageWidget,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        displayName: "SE Detail Image Widget",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Tittle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
                sortOrder: 0,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },

            {
                fieldType: "Translatable",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.backgroundcolor,
                displayName: "Change Background Color",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Yes", value: "yes" },
                    { displayName: "No", value: "no" },
                ],
                defaultValue: "left",
            },
            {
                fieldType: "Translatable",
                name: fields.position,
                displayName: "Change Image Position",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
            },
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.buttonurl,
                displayName: "Button URL",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
        ],
    },
};

export default detailImageWidget;
